/**
 * Voice-log: speak a bedtime note or dream fragment. Transcribed via Web Speech.
 * Stored locally in OPFS op-log. Privacy promise: audio never persists.
 */
import { listenOnce, speechSupported } from '../chassis/voice.ts';
import { grant, hasConsent } from '../chassis/consent.ts';
import { appendOp } from '../chassis/storage-aaa.ts';

const TPL = /* html */ `
<style>
  :host {
    display: block;
    margin: 28px 0;
  }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.12);
    border-radius: 14px;
    padding: 22px 24px;
    background: linear-gradient(180deg, oklch(11% 0.04 270 / 0.55), oklch(8% 0.03 268 / 0.65));
    box-shadow: 0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(22px, 5vw, 26px);
    color: oklch(94% 0.04 88);
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 18px;
    font-size: 13px;
  }
  .pulse {
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: radial-gradient(circle, oklch(86% 0.14 82 / 0.9), oklch(70% 0.10 80 / 0.5));
    margin: 16px auto;
    cursor: pointer;
    transition: transform 0.12s ease;
  }
  .pulse.live {
    animation: pulse 1.4s ease-in-out infinite;
  }
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.08); opacity: 0.75; }
  }
  .transcript {
    font-family: var(--display);
    font-style: italic;
    color: oklch(82% 0.05 60);
    text-align: center;
    margin-top: 12px;
    min-height: 22px;
  }
  .history {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid oklch(80% 0.04 80 / 0.1);
    font-family: var(--display);
    font-size: 13px;
    color: oklch(76% 0.04 80);
  }
  .entry {
    margin: 6px 0;
    padding-left: 14px;
    border-left: 1px solid oklch(80% 0.04 80 / 0.15);
  }
  .entry-time {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: oklch(60% 0.04 60);
  }
</style>

<div class="card">
  <div class="eyebrow">Aramgah · Voice journal</div>
  <h3>Khwaab ya khayaal record karein</h3>
  <div class="sub">Web Speech · audio kahin save nahi · sirf text aapke phone par</div>

  <div class="pulse" id="pulse" role="button" tabindex="0" aria-label="Tap to record"></div>
  <div class="transcript" id="transcript">—</div>

  <div class="history" id="history"></div>
</div>
`;

const HISTORY_KEY = 'aramgah:voice-log-v1';

interface VoiceEntry {
  ts: string;
  text: string;
}

export class AramgahVoiceLog extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #recording = false;

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const pulse = this.#shadow.getElementById('pulse');
    pulse?.addEventListener('click', () => { void this.#toggle(); });
    pulse?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
        e.preventDefault();
        void this.#toggle();
      }
    });
    this.#renderHistory();
    if (!speechSupported()) {
      const t = this.#shadow.getElementById('transcript');
      if (t) t.textContent = 'voice unsupported here · type instead';
    }
  }

  async #toggle(): Promise<void> {
    if (this.#recording) return;
    if (!speechSupported()) return;
    if (!hasConsent('microphone_voice')) {
      const ok = confirm('Microphone use to transcribe your voice locally. Audio is not saved. OK?');
      if (!ok) return;
      grant('microphone_voice', 'voice-log consent');
    }
    this.#recording = true;
    const pulse = this.#shadow.getElementById('pulse');
    pulse?.classList.add('live');
    const t = this.#shadow.getElementById('transcript');
    if (t) t.textContent = 'sun raha hoon...';
    try {
      const text = await listenOnce({
        lang: 'hi-IN',
        onPartial: (p) => {
          if (t) t.textContent = p;
        },
      });
      if (text) {
        await this.#save(text);
        if (t) t.textContent = text;
      } else {
        if (t) t.textContent = '—';
      }
    } catch (e) {
      if (t) t.textContent = `${(e as Error).message}`;
    } finally {
      this.#recording = false;
      pulse?.classList.remove('live');
    }
  }

  async #save(text: string): Promise<void> {
    const entry: VoiceEntry = { ts: new Date().toISOString(), text };
    const hist = loadHistory();
    hist.unshift(entry);
    if (hist.length > 30) hist.length = 30;
    saveHistory(hist);
    await appendOp('aramgah', { path: ['voice_log'], value: entry });
    this.#renderHistory();
  }

  #renderHistory(): void {
    const wrap = this.#shadow.getElementById('history');
    if (!wrap) return;
    const hist = loadHistory().slice(0, 5);
    if (hist.length === 0) {
      wrap.innerHTML = '<div style="font-style:italic;color:oklch(60% 0.04 60)">koi record nahi · pulse tap karein</div>';
      return;
    }
    wrap.innerHTML = hist.map((e) => `
<div class="entry">
  <div class="entry-time">${shortTime(e.ts)}</div>
  <div>${escapeHtml(e.text)}</div>
</div>`).join('');
  }
}

function loadHistory(): VoiceEntry[] {
  const raw = localStorage.getItem(HISTORY_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as VoiceEntry[];
  } catch {
    return [];
  }
}
function saveHistory(h: VoiceEntry[]): void {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(h));
}
function shortTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('aramgah-voice-log', AramgahVoiceLog);
